import {
    DEFAULT_COORDINATES_CONSTRAINTS,
    getWidgetCreationCoordinates,
    NewWidgetType,
    WidgetCoordinates,
} from 'adapter/in/dashboards/components/Grid/newWidgetOverlayUtils';
import Layout, { LayoutElement, LayoutElementType, LayoutType } from 'domain/entity/layout';

export type ReportAnalysisCoordinates = {
    x: number;
    y: number;
    w: number;
    h: number;
};

const COORDINATES_QUERY_PARAM = {
    layoutType: 'coordinatesLayoutType',
    x: 'coordinatesX',
    y: 'coordinatesY',
    w: 'coordinatesW',
    h: 'coordinatesH',
} as const;

export const buildReportAnalysisSearch = (contextPeriod?: string, coordinates?: ReportAnalysisCoordinates): string => {
    const params = new URLSearchParams();

    if (contextPeriod !== undefined) {
        params.set('context', contextPeriod);
    }

    if (coordinates) {
        params.set(COORDINATES_QUERY_PARAM.layoutType, LayoutType.A4);
        params.set(COORDINATES_QUERY_PARAM.x, String(coordinates.x));
        params.set(COORDINATES_QUERY_PARAM.y, String(coordinates.y));
        params.set(COORDINATES_QUERY_PARAM.w, String(coordinates.w));
        params.set(COORDINATES_QUERY_PARAM.h, String(coordinates.h));
    }

    const query = params.toString();

    return query ? `?${query}` : '';
};

export const parseReportAnalysisCoordinates = (search: string): ReportAnalysisCoordinates | null => {
    const params = new URLSearchParams(search);
    const layoutType = params.get(COORDINATES_QUERY_PARAM.layoutType);

    if (layoutType === null) return null;
    if (layoutType !== LayoutType.A4) return null;

    const x = parseIntegerParam(params.get(COORDINATES_QUERY_PARAM.x));
    const y = parseIntegerParam(params.get(COORDINATES_QUERY_PARAM.y));
    const w = parseIntegerParam(params.get(COORDINATES_QUERY_PARAM.w));
    const h = parseIntegerParam(params.get(COORDINATES_QUERY_PARAM.h));

    if (x === null || y === null || w === null || h === null || w <= 0 || h <= 0) {
        return null;
    }

    return { x, y, w, h };
};

export const addReportAnalysisToA4Layout = (layouts: Layout[], analysisId: string, coordinates: ReportAnalysisCoordinates): Layout => {
    const a4Layout = layouts.find((layout) => layout.type === LayoutType.A4);
    const widgets = a4Layout?.widgets.filter((widget) => widget.id !== analysisId) ?? [];
    const creationCoordinates = getWidgetCreationCoordinates(widgets.map(asWidgetCoordinates), NewWidgetType.ANALYSIS, coordinates);

    return new Layout(LayoutType.A4, [...widgets, buildReportAnalysisLayoutElement(analysisId, creationCoordinates)]);
};

const buildReportAnalysisLayoutElement = (analysisId: string, coordinates: ReportAnalysisCoordinates): LayoutElement => ({
    id: analysisId,
    position: {
        x: coordinates.x,
        y: coordinates.y,
    },
    size: {
        width: coordinates.w,
        height: coordinates.h,
    },
    type: LayoutElementType.ANALYSIS,
    constraints: DEFAULT_COORDINATES_CONSTRAINTS,
});

const asWidgetCoordinates = (element: LayoutElement): WidgetCoordinates => ({
    x: element.position.x,
    y: element.position.y,
    w: element.size.width,
    h: element.size.height,
});

const parseIntegerParam = (value: string | null): number | null => {
    if (value === null) return null;

    const parsedValue = Number(value);

    return Number.isInteger(parsedValue) ? parsedValue : null;
};
